import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { formatPrice, formatDate, getStatusText } from '../lib/utils';
import { toast } from 'sonner';
import { Search, Package, Clock, CheckCircle2, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TrackOrder() {
  const [orderNumber, setOrderNumber] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!orderNumber.trim()) {
      toast.error('Please enter an order number');
      return;
    }

    setLoading(true);
    setSearched(true);
    try {
      const response = await axios.get(`${API_URL}/api/orders/track/${orderNumber.trim()}`);
      setOrder(response.data.order);
    } catch (error) {
      console.error('Order not found:', error);
      setOrder(null);
      toast.error('Order not found');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-400" />;
      case 'processing':
        return <Package className="w-5 h-5 text-cyan-400" />;
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-emerald-400" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-400" />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12 bg-[#09090b] text-zinc-50 relative flex items-center">
      <div className="absolute inset-0 bg-[radial-gradient(#1c1c1f_1px,transparent_1px)] [background-size:16px_16px] opacity-10 pointer-events-none" />
      <div className="max-w-xl mx-auto px-4 w-full relative z-10">
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="font-mono text-[10px] text-emerald-400 font-semibold tracking-wider uppercase">Order Tracker</span>
          </div>
          <h1 className="font-bold text-2xl text-white mb-2">
            Track Transaction
          </h1>
          <p className="text-zinc-400 text-sm">
            Enter your order reference number to check status.
          </p>
        </div>

        {/* Search Input Card */}
        <form onSubmit={handleSearch} className="bg-zinc-900/30 rounded-xl p-6 border border-zinc-850 mb-8">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="orderNumber" className="text-xs text-zinc-400 font-medium">Order Number</Label>
              <div className="flex gap-3">
                <Input
                  id="orderNumber" type="text"
                  placeholder="e.g., NFV2026..."
                  className="bg-zinc-950 border-zinc-800 text-white flex-1 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/40 text-sm rounded-lg"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  data-testid="track-order-input"
                />
                <Button type="submit"
                  className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-4"
                  disabled={loading} data-testid="track-order-btn">
                  {loading
                    ? <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                    : <Search className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>
        </form>

        {/* Result */}
        {searched && !loading && (
          order ? (
            <div className="bg-zinc-900/30 rounded-xl border border-zinc-850 overflow-hidden">
              {/* Status Header */}
              <div className="p-5 border-b border-zinc-850 bg-zinc-950/40">
                <div className="flex items-center gap-4">
                  {getStatusIcon(order.status)}
                  <div>
                    <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">Status</p>
                    <p className={`font-bold text-sm uppercase tracking-wider ${
                      order.status === 'completed' ? 'text-emerald-400' :
                      order.status === 'pending' ? 'text-yellow-400' :
                      order.status === 'processing' ? 'text-cyan-400' : 'text-red-400'
                    }`}>
                      {getStatusText(order.status)}
                    </p>
                  </div>
                  <div className="ml-auto font-mono text-xs text-zinc-400">{order.order_number}</div>
                </div>
              </div>

              {/* Details */}
              <div className="p-5 space-y-3">
                {[
                  { label: 'Order Number', val: order.order_number, mono: true },
                  { label: 'Order Date', val: formatDate(order.created_at) },
                  { label: 'Product', val: order.product_name },
                  { label: 'Item', val: order.denomination_name },
                  { label: 'User ID', val: order.game_user_id, mono: true },
                  ...(order.game_server_id ? [{ label: 'Server ID', val: order.game_server_id, mono: true }] : []),
                ].map(row => (
                  <div key={row.label} className="flex justify-between items-start py-1.5 border-b border-zinc-850 text-xs">
                    <span className="text-zinc-500">{row.label}</span>
                    <span className={`text-white font-medium ${row.mono ? 'font-mono' : ''}`}>{row.val}</span>
                  </div>
                ))}
                <div className="flex justify-between items-center pt-2">
                  <span className="text-zinc-550 text-xs font-semibold">TOTAL</span>
                  <span className="font-bold text-white text-base">{formatPrice(order.price)}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-zinc-900/30 rounded-xl p-8 border border-zinc-850 text-center">
              <XCircle className="w-10 h-10 text-red-500 mx-auto mb-4" />
              <p className="text-white font-semibold text-sm mb-2">Order Not Found</p>
              <p className="text-zinc-400 text-xs">No matching order details found in our systems.</p>
            </div>
          )
        )}
      </div>
    </div>
  );
}
